// src/index.js - FIXED VERSION (keeps all your existing functionality)
import React, { useState, useEffect, useMemo, createContext } from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import './index.css';
import './mobile.css'; // Import your enhanced mobile styles
import { register as registerSW } from './serviceWorkerRegistration';

// Create a context for accessing theme toggle functionality throughout the app
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
  mode: 'light'
});

// Mobile optimization setup component - kept intact from original
const MobileOptimization = () => {
  useEffect(() => {
    // Set proper viewport meta tag
    const setViewportMeta = () => {
      let viewport = document.querySelector('meta[name="viewport"]');
      if (!viewport) {
        viewport = document.createElement('meta');
        viewport.name = 'viewport';
        document.head.appendChild(viewport);
      }
      viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=5.0, viewport-fit=cover';
    };

    // Add keyboard detection for handling mobile keyboard appearance
    const setupKeyboardDetection = () => {
      const appRoot = document.getElementById('root');
      if (!appRoot) return;
      
      // Visual viewport API for modern browsers
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', () => {
          const isKeyboardOpen = window.visualViewport.height < window.innerHeight;
          if (isKeyboardOpen) {
            appRoot.classList.add('keyboard-open');
          } else {
            appRoot.classList.remove('keyboard-open');
          }
        });
      }
    };
    
    // Enable momentum scrolling on iOS
    const enableMomentumScrolling = () => {
      document.addEventListener('touchmove', function(event) {
        // Allow default scrolling behavior
      }, { passive: true });
    };
    
    // Add double-tap prevention for iOS
    const preventDoubleTapZoom = () => {
      let lastTouchEnd = 0;
      document.addEventListener('touchend', function(event) {
        const now = Date.now();
        if (now - lastTouchEnd <= 300) {
          event.preventDefault();
        }
        lastTouchEnd = now;
      }, false);
    };

    // Execute optimizations
    setViewportMeta();
    setupKeyboardDetection();
    enableMomentumScrolling();
    preventDoubleTapZoom();
    
    // Optional: Add body class based on device type for specific styling
    const addDeviceClasses = () => {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      
      if (isMobile) document.body.classList.add('mobile-device');
      if (isIOS) document.body.classList.add('ios-device');
    };
    
    addDeviceClasses();
  }, []);

  return null; // This component doesn't render anything
};

// Main Root component that manages theme state
function Root() {
  // Initialize theme state, preferring saved preference if available
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode || 'light';
  });
  
  // Function to toggle between light and dark mode
  const toggleColorMode = () => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', newMode);
      return newMode;
    });
  };
  
  // Create a theme based on current mode
  const theme = useMemo(() => {
    // Define the base theme with common properties
    const baseTheme = {
      palette: {
        mode: mode,
        primary: {
          main: '#2196f3',
        },
        secondary: {
          main: '#f50057',
        },
        success: {
          main: '#4caf50',
        },
        error: {
          main: '#f44336',
        },
        warning: {
          main: '#ff9800',
        },
      },
      typography: {
        fontFamily: [
          '-apple-system',
          'BlinkMacSystemFont',
          '"Segoe UI"',
          'Roboto',
          '"Helvetica Neue"',
          'Arial',
          'sans-serif',
        ].join(','),
      },
      shape: {
        borderRadius: 8,
      },
      transitions: {
        duration: {
          shortest: 150,
          shorter: 200,
          short: 250,
          standard: 300,
        },
      },
    };
    
    // Apply mode-specific palette values
    if (mode === 'light') {
      baseTheme.palette = {
        ...baseTheme.palette,
        background: {
          default: '#f8fafc',
          paper: '#ffffff',
        },
        text: {
          primary: '#2c3e50',
          secondary: '#546e7a',
        },
        divider: 'rgba(0, 0, 0, 0.12)',
      };
    } else {
      baseTheme.palette = {
        ...baseTheme.palette,
        background: {
          default: '#121212',
          paper: '#1e1e1e',
        },
        text: {
          primary: '#e0e0e0',
          secondary: '#aaaaaa',
        },
        divider: 'rgba(255, 255, 255, 0.12)',
      };
    }
    
    // Add component overrides with theme-aware styling
    baseTheme.components = {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: 'background-color, color',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: mode === 'light' 
              ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              : '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.18)',
            borderRadius: 8,
            transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: 'background-color',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: 'background-color',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), color 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.1s cubic-bezier(0.4, 0, 0.2, 1)',
            // Improve touch target sizes
            '@media (max-width: 600px)': {
              minHeight: 44,
              padding: '8px 16px',
            },
          },
        },
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            // Enhanced styles for bottom navigation
            '@media (max-width: 600px)': {
              height: 56,
            },
            transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: 'background-color',
          },
        },
      },
      // Enhanced dialog for mobile
      MuiDialog: {
        styleOverrides: {
          paper: {
            '@media (max-width: 480px)': {
              margin: 16,
              borderRadius: 16,
            },
            transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: 'background-color',
          },
        },
      },
      // Enhanced drawer for mobile
      MuiDrawer: {
        styleOverrides: {
          paper: {
            '@media (max-width: 480px)': {
              width: '85%', // Wider by default on small screens
            },
            transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: 'background-color',
          },
        },
      },
      // Better touch feedback for buttons
      MuiTouchRipple: {
        styleOverrides: {
          root: {
            '@media (pointer: coarse)': {
              opacity: 0.8,
            },
          },
        },
      },
      // Added specific styling for list items in dark mode
      MuiListItem: {
        styleOverrides: {
          root: {
            transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          },
        },
      },
      // Added specific styling for tabs
      MuiTab: {
        styleOverrides: {
          root: {
            transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          },
        },
      },
      // Added specific styling for inputs
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          },
        },
      },
    };
    
    // Create the theme with responsive font sizes
    return responsiveFontSizes(createTheme(baseTheme));
  }, [mode]);
  
  // Create context value with theme toggle function
  const colorModeContextValue = useMemo(
    () => ({
      toggleColorMode,
      mode,
    }),
    [mode]
  );
  
  return (
    <ColorModeContext.Provider value={colorModeContextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <MobileOptimization />
        <App />
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));

// 🚀 CRITICAL FIX: Conditionally disable StrictMode in development to prevent duplicate uploads
const isDevelopment = process.env.NODE_ENV === 'development';
const forceStrictMode = process.env.REACT_APP_FORCE_STRICT_MODE === 'true';

if (isDevelopment && !forceStrictMode) {
  // Development mode without StrictMode to prevent duplicate effect execution
  console.log('🛠️ Running in development mode without StrictMode to prevent duplicate receipt uploads');
  console.log('💡 To enable StrictMode in dev, set REACT_APP_FORCE_STRICT_MODE=true');
  
  root.render(
    <HelmetProvider>
      <Root />
    </HelmetProvider>
  );
} else {
  // Production mode or forced StrictMode
  console.log('⚡ Running with StrictMode enabled');
  
  root.render(
    <React.StrictMode>
      <HelmetProvider>
        <Root />
      </HelmetProvider>
    </React.StrictMode>
  );
}

// Register service worker for offline mode
registerSW();